/** What a comment thread is waiting for (RFC 0324 3d, 3g). */

import { registryStatus } from '../session/reads'
import type { CommentThread } from './reads'

export type ThreadPendingState = 'reading' | 'working' | 'read'

export interface ThreadPending {
  state: ThreadPendingState
  /** The session the thread is waiting on. */
  sessionId: string
}

/**
 * What this thread is waiting for. Worked out on read, never stored, and from
 * the project registry alone, so drawing it opens no session.
 *
 * `candidates` are the sessions the last comment could have woken, in the
 * order to trust them: the ones it named, the one a reply wakes, the space's.
 * The caller knows the space; the thread does not.
 *
 * Only a status written AFTER the last comment counts. An older one was about
 * something else, and showing it would light up every thread on the page.
 * Queued reads as reading, running or asking as working. "Read" comes only
 * from the session passing on this comment (3g), never from an idle status:
 * a host writes idle whenever it starts mirroring a session, after a daemon
 * restart for one, and that idle is not a turn that read anything. When a
 * session wrote the last comment its reply has landed, and the thread shows
 * nothing.
 */
export function threadPending(
  thread: CommentThread,
  projectDoc: unknown,
  candidates: ReadonlyArray<string>,
  now: number = Date.now(),
): ThreadPending | null {
  const last = thread.replies[thread.replies.length - 1] ?? thread.root
  if (last.authorSessionId) return null
  const meta =
    (projectDoc as { sessionMeta?: Record<string, unknown> } | undefined)
      ?.sessionMeta ?? {}
  for (const sessionId of candidates) {
    // Passed on THIS comment: read, whatever else the session is doing. Its
    // status is the whole session's, and a session busy with something else
    // is not working on this comment.
    const readAt = last.readBy?.[sessionId]
    if (typeof readAt === 'number' && readAt >= last.createdAt) {
      return { state: 'read', sessionId }
    }
    const entry = meta[sessionId] as
      | { status?: unknown; statusAt?: unknown }
      | undefined
    const at = entry?.statusAt
    if (typeof at !== 'number' || at < last.createdAt) continue
    const status = registryStatus(entry, now)
    if (status === 'queued') return { state: 'reading', sessionId }
    if (status === 'running' || status === 'asking') {
      return { state: 'working', sessionId }
    }
  }
  return null
}

/**
 * Whether a thread is waiting on this person (RFC 0324 3f): open, and its last
 * comment written by somebody else. Your own unanswered comment is waiting on
 * them, not on you. A null person is the window that has not said who it is,
 * which wrote its comments as the old `'user'`. Those comments stay yours
 * after you give a name: they were written before anyone had one, and
 * counting them as someone else's put your own threads in your count.
 */
export function isWaitingOn(
  thread: CommentThread,
  personId: string | null,
): boolean {
  if (thread.root.resolved) return false
  const last = thread.replies[thread.replies.length - 1] ?? thread.root
  if (last.authorSessionId) return true
  if (last.author === 'user') return false
  return last.author !== personId
}
